import { useEffect, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ReactComponent as DownArrowSvg } from '../assets/down-arrow.svg';
import { BodySmall, HeadingMedium } from './typography';

const StyledCarousel = styled(Carousel)(() => ({
  maxWidth: '100vw',
  '& .carousel-slider': { height: '60vh' },
}));

export default function ProductDetailsCarousel ({
  dispatch,
  state
}) {
  const { vtCurrentProducts, vtSelectedHotspot } = state;
  
  const [carouselState, setCarouselState] = useState({
    currentSlide: 1,
    showCarousel: false,
  });

  const [delayedShowCarousel, setDelayedShowCarousel] = useState(
    carouselState.showCarousel
  );
  useEffect(() => {
    setDelayedShowCarousel(carouselState.showCarousel);
  }, [carouselState]);

  useEffect(() => {
    const showCarousel = !!vtSelectedHotspot;
    let currentSlide = 1;
    if (showCarousel) {
      const selectedProductIndex = vtCurrentProducts.findIndex(product => product.id === vtSelectedHotspot.propInfo.id);
      currentSlide = selectedProductIndex > 0 ? (selectedProductIndex + 1) : 1;
    }
    setCarouselState(prevState => ({
      currentSlide: showCarousel ? currentSlide : prevState.currentSlide,
      showCarousel,
    }));
  }, [vtCurrentProducts, vtSelectedHotspot]);

  const updateCurrentSlide = (index) => {
    if (carouselState.currentSlide !== index) {
      setCarouselState({
        ...carouselState,
        currentSlide: index,
      });
    }
  };

  const handleClose = () => {
    dispatch({ type: 'close-product-carousel-clicked' });
  };

  const isLargeViewport = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  return (
    <Dialog
      fullScreen
      open={carouselState.showCarousel}
      onClose={handleClose}
      className={delayedShowCarousel ? 'delayed-open' : ''}
      transitionDuration={500}
      sx={{
        '& .MuiPaper-root, & .MuiBackdrop-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },

        '&': {
          opacity: '0',
          transform: 'translateY(30%)',
          transition: 'opacity 500ms ease-in-out, transform 350ms ease-in-out',
        },
        '&.delayed-open': {
          opacity: '1',
          transform: 'translateY(0)',
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 0,
        }}
      >
        <StyledCarousel
          autoPlay={false}
          autoFocus={true}
          centerMode={true}
          dynamicHeight={false}
          emulateTouch={true}
          infiniteLoop={false}
          onChange={updateCurrentSlide}
          selectedItem={carouselState.currentSlide}
          showArrows={false}
          showIndicators={false}
          showStatus={false}
          showThumbs={false}
          swipeable={true}
          useKeyboardArrows={true}
        >
          <HiddenSlide />
          {vtCurrentProducts?.map((product, index) => (
            <ProductDetails
              key={index}
              isCurrentSlide={index + 1 === carouselState.currentSlide}
              isLargeViewport={isLargeViewport}
              onClose={handleClose}
              product={product}
            />
          ))}
          <HiddenSlide />
        </StyledCarousel>
      </DialogContent>
    </Dialog>
  );
};

function HiddenSlide () {
  return (
    <Paper
      elevation={2}
      sx={{
        backgroundColor: 'transparent !important',
        boxShadow: 'none',
        height: '60vh',
        transform: 'scale(0.90)',
        width: '100%',
      }}
    />
  );
}

const productMetadataCommonStyles = {
  mx: { xs: 4, lg: 7 },
};

function ProductDetails ({ onClose, product, isCurrentSlide, isLargeViewport }) {
  const imgPath = product?.image?.split?.('modsy/')?.[1];
  const src = imgPath ? `https://cache.modsy.com/imgresize/${imgPath}?w=1024` : (product?.image || '');
  let productFeatures = [];
  if (product?.source) {
    productFeatures.push(`Brand: ${product.source}`);
  }
  if (product?.color) {
    productFeatures.push(`Color: ${product.color}`);
  }
  if (product?.fabric) {
    productFeatures.push(`Fabric: ${product.fabric}`);
  }
  if (product?.finish) {
    productFeatures.push(`Finish: ${product.finish}`);
  }
  if (product?.height || product?.width || product?.depth) {
    let dimStr = 'Dimensions: ';
    if (product?.height) {
      dimStr += `${product.height}"H`;
    }
    if (product?.width) {
      dimStr += `${product?.height ? ' x ' : ''}${product.width}"W`;
    }
    if (product?.depth) {
      dimStr += `${(product?.height || product?.width) ? ' x ' : ''}${product.depth}"D`;
    }
    productFeatures.push(dimStr);
  }

  return (
    <Paper
      elevation={2}
      sx={{
        backgroundColor: '#FFFFFF !important',
        height: '60vh',
        transform: isCurrentSlide ? 'scale(1.0)' : 'scale(0.90)',
        opacity: isCurrentSlide ? 1 : 0.95,
        width: '100%',
        transition: 'all .35s ease-in-out',
      }}
    >
      <Grid
        container
        sx={{
          height: '100%',
        }}
      >
        <Grid item xs={6}
          sx={{
            alignItems: 'center',
            backgroundColor: '#F6F6F6',
            display: 'flex',
            height: '100%',
            padding: 4,
          }}
        >
          <Box
            component='img'
            src={src}
            alt=''
            sx={{
              maxHeight: '50vh',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          />
        </Grid>
        <Grid item xs={6}
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            position: 'relative',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack
            direction='column'
            textAlign='left'
            sx={{
              width: '100%',
              pt: 4,
            }}
          >
            <IconButton
              aria-label='close'
              onClick={onClose}  
              sx={{
                position: 'absolute',
                right: {xs: '0.5rem', lg: '1rem' },
                top: { xs: '0.5rem', lg: '1rem' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <HeadingMedium
              sx={{
                ...productMetadataCommonStyles,
                mb: 3,
              }}
            >
              {product?.name}
            </HeadingMedium>
            <BodySmall
              sx={{
                ...productMetadataCommonStyles,
                color: '#646468',
                lineHeight: '1.375rem',
                mb: 5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: isLargeViewport ? 4 : 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {product?.description || ''}
            </BodySmall>
            <Separator />
            <AccordianSection
              defaultExpanded={isLargeViewport}
              details={productFeatures.join('\n')}
              summary='Features'
            />
            <Separator mt={2} />
            <AccordianSection
              defaultExpanded={isLargeViewport}
              details='Manufacter warrranty applies.'
              summary='Warranty'
            />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

function Separator ({ mt = 0 }) {
  return (
    <Box
      sx={{
        ...productMetadataCommonStyles,
        border: '1px solid rgba(0, 0, 0, 0.2)',
        height: 0,
        mb: 2,
        mt,
      }}
    />
  );
}

function AccordianSection ({ defaultExpanded, details, summary }) {
  return (
    <Accordion
      elevation={0}
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        ...productMetadataCommonStyles,
        backgroundColor: 'transparent !important',
        border: 'none !important',
        boxShadow: 'none !important',
        p: 0,
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<DownArrowSvg />}
        aria-controls={`${summary}-content`}
        id={`${summary}-header`}
        sx={{
          p: 0,
        }}
      >
        <HeadingMedium>{summary}</HeadingMedium>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          whiteSpace: 'pre-wrap',
          padding: 0,
        }}
      >
        <BodySmall
          sx={{
            color: '#646468',
            lineHeight: '1.375rem',
          }}
        >
          {details}
        </BodySmall>
      </AccordionDetails>
    </Accordion>
  );
}


